import React, { useEffect, useState } from "react";
import ChairIcon from "@mui/icons-material/Chair";
import { Button } from "@mui/material";
import LibrarySeatManager from "../pages/LibrarySeatManager";
import SwitchRequestModal from "./SwitchRequestModal";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

// CSS animation added via useEffect
const pulseStyle = `
@keyframes pulse {
  0%   { transform: scale(1); }
  50%  { transform: scale(1.1); }
  100% { transform: scale(1); }
}
`;

const SeatMap = ({
  data = [],
  student,
  onSeatClick,
  isAdminView = false,
  refreshData,
  viewMode = "seat",
}) => {
  const [openSeatManager, setOpenSeatManager] = useState(false);
  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const [currentBookingForSwitch, setCurrentBookingForSwitch] = useState(null);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = pulseStyle;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Library-only grouping (no hostel rooms/beds)
  const grouped = data.reduce((acc, item) => {
    const group = item.section?.section_name || "Study Centre Seats";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const getColor = (status = "", isBookedByCurrentUser = false) => {
    switch (status.toLowerCase()) {
      case "booked":
        return "#F44336";
      case "pending":
        return isBookedByCurrentUser ? "#FF9800" : "#4CAF50";
      default:
        return "#4CAF50";
    }
  };

  const handleSwitchClick = (item, e) => {
    e.stopPropagation();
    setCurrentBookingForSwitch(item);
    setSwitchModalOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Legend + Admin Add Seat */}
      <div className="flex justify-between">
        <div className="flex justify-end gap-6 text-sm font-medium text-gray-700">
          {[
            { label: "Available", color: "#4CAF50" },
            { label: "Pending", color: "#FF9800" },
            { label: "Booked", color: "#F44336" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: item.color }}
              ></div>
              {item.label}
            </div>
          ))}
        </div>

        {isAdminView && (
          <div>
            <div className="bg-primaryColor text-white rounded shadow inline-block px-3">
              <Button
                onClick={() => setOpenSeatManager(true)}
                className="!text-white !p-2"
              >
                + Add Seat
              </Button>
            </div>
            <LibrarySeatManager
              open={openSeatManager}
              onClose={() => setOpenSeatManager(false)}
              onSuccess={() => {
                refreshData?.();
                setOpenSeatManager(false);
              }}
            />
          </div>
        )}
      </div>

      {/* No Data */}
      {Object.keys(grouped).length === 0 ? (
        <div className="text-center text-gray-500 text-lg mt-10">
          🚫 No seats available
        </div>
      ) : (
        <div
          className={`grid ${
            Object.keys(grouped).includes("Study Centre Seats") &&
            Object.keys(grouped).length === 1
              ? "grid-cols-1"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          } gap-4 sm:gap-6`}
        >
          {Object.entries(grouped).map(([group, items], index) => (
            <div
              key={index}
              className="border border-gray-300 rounded-md p-4 bg-white"
            >
              <h3 className="text-lg font-semibold mb-2">{group}</h3>
              <hr className="h-px bg-gray-200 border-0 mb-4" />

              <div
                className={`grid ${
                  Object.keys(grouped).includes("Study Centre Seats") &&
                  Object.keys(grouped).length === 1
                    ? "grid-cols-2 md:grid-cols-8"
                    : "grid-cols-2 sm:grid-cols-3"
                } gap-2 sm:gap-4`}
              >
                {items.map((item, idx) => {
                  const status = item.status?.toLowerCase();
                  const isPending = status === "pending";
                  const isAvailable = status === "available";

                  // library booking logic
                  const hasLibraryBooking =
                    student?.library_booking_id &&
                    student.library_booking_id !== item.booking_id;

                  const isBookedByCurrentUser =
                    student?.library_booking_id &&
                    student.library_booking_id === item.booking_id;

                  const isCurrentBooking = isBookedByCurrentUser;
                  const isBlockedByExistingBooking = hasLibraryBooking;

                  const isClickable = isAdminView
                    ? !!item.booking_id
                    : (isAvailable || isPending) &&
                      !isBlockedByExistingBooking &&
                      !isCurrentBooking;

                  return (
                    <div
                      key={idx}
                      onClick={() => isClickable && onSeatClick?.(item)}
                      className={`border rounded-xl w-full flex flex-col items-center shadow transition-transform duration-200 p-2 sm:p-3 relative group
                        ${
                          isBookedByCurrentUser
                            ? "bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-400 shadow-lg"
                            : "bg-white"
                        }
                        ${
                          isClickable
                            ? "cursor-pointer hover:scale-105 hover:shadow-lg"
                            : "cursor-not-allowed"
                        }
                      `}
                    >
                      {isBookedByCurrentUser && (
                        <>
                          <span
                            title="Your booking"
                            className="mr-1 absolute top-1 right-1 z-10 bg-blue-600 text-white rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                          >
                            YOU
                          </span>
                          {!isAdminView && (
                            <button
                              onClick={(e) => handleSwitchClick(item, e)}
                              title="Switch Seat"
                              className="absolute bottom-1 right-1 z-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full p-1 transition-colors duration-200"
                            >
                              <SwapHorizIcon sx={{ fontSize: 12 }} />
                            </button>
                          )}
                        </>
                      )}

                      {isPending &&
                        item.pending_count &&
                        !isBookedByCurrentUser && (
                          <span
                            title={`${item.pending_count} people applied for this.`}
                            className="mr-1 absolute top-1 right-1 z-10 bg-[#FF9800] text-white rounded-full px-2 text-center text-[14px]"
                          >
                            {item.pending_count}
                          </span>
                        )}

                      {/* Icon + Seat number */}
                      <div className="flex relative flex-col items-center justify-center mt-2 sm:mt-4 mb-1 sm:mb-2">
                        <ChairIcon
                          style={{
                            width: "32px",
                            height: "32px",
                            fill: getColor(status, isBookedByCurrentUser),
                          }}
                          className="sm:w-9 sm:h-9"
                        />
                        <div
                          className={`mt-1 sm:mt-2 text-sm sm:text-base font-semibold ${
                            isBookedByCurrentUser
                              ? "text-blue-700"
                              : "text-gray-800"
                          }`}
                        >
                          {item.seat_number || "?"}
                        </div>
                      </div>

                      {item.description && (
                        <div
                          className="mt-1 text-xs text-gray-500 text-center w-full truncate"
                          title={item.description}
                        >
                          {item.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Switch Request Modal (library only) */}
      <SwitchRequestModal
        open={switchModalOpen}
        onClose={() => setSwitchModalOpen(false)}
        currentBooking={currentBookingForSwitch}
        bookingType="library"
        student={student}
        onSuccess={() => {
          refreshData?.();
          setSwitchModalOpen(false);
        }}
      />
    </div>
  );
};

export default SeatMap;
